import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { TimeService } from './time.service';
import { ContextService } from './context.service';
import { PeProviderService } from './pe-provider.service';
import { DataType, DimosDataType, RowDataType } from './data.service.types';

@Injectable()
export class DataService {

  rawData: DataType[] = [];
  dataPerDimos: DimosDataType = {};


  private maxValueMap: { group: string, name: string, value: number }[] = [];

  constructor(private api: ApiService, private time: TimeService, private app: ContextService, private peProvider: PeProviderService) {
    this.load();
  }

  async load() {
    this.rawData = await this.api.get('/kounoupia/data');

    const uniqueDimoi: number[] = [];
    const uniqueDimoiName: string[] = [];
    this.rawData.forEach(row => {
      const index = uniqueDimoi.indexOf(row.hlpcode);
      if (index === -1) {
        uniqueDimoi.push(row.hlpcode);
        uniqueDimoiName.push(row.dimos_name);
      }
    });



    const uniqueYear: number[] = [];
    this.rawData.forEach(row => {
      const index = uniqueYear.indexOf(row.year);
      if (index === -1) {
        uniqueYear.push(row.year);
      }
    });

    this.time.set(uniqueYear);

    uniqueDimoi.forEach((hlpcode, index) => {
      this.dataPerDimos[hlpcode] = { name: uniqueDimoiName[index] };
      uniqueYear.forEach(year => {
        this.dataPerDimos[hlpcode][year] = {};
        for (let i = 1; i <= 12; i++) {
          this.dataPerDimos[hlpcode][year][i] = {
            agrotiko: {
              kanalia: 0,
              loipes_ydatosylloges: 0,
              pigadia: 0,
              remata: 0
            },
            astiko: {
              freatia: 0,
              loipes_ydatosylloges: 0,
              pigadia: 0,
              vothroi: 0
            },
            fysiko_systima: {
              plimirismenes_ektaseis: 0
            }
          };
        }
      });
    });



    this.rawData.forEach(row => {
      const datum = this.dataPerDimos[row.hlpcode][row.year];

      Object.keys(row.month).forEach(month => {
        const monthIndex = parseInt(month, 10);
        const monthDatum = datum[monthIndex];

        monthDatum.agrotiko.kanalia += row.month[monthIndex].agrotiko.kanalia || 0;
        monthDatum.agrotiko.loipes_ydatosylloges += row.month[monthIndex].agrotiko.loipes_ydatosylloges || 0;
        monthDatum.agrotiko.pigadia += row.month[monthIndex].agrotiko.pigadia || 0;
        monthDatum.agrotiko.remata += row.month[monthIndex].agrotiko.remata || 0;

        monthDatum.astiko.freatia += row.month[monthIndex].astiko.freatia || 0;
        monthDatum.astiko.loipes_ydatosylloges += row.month[monthIndex].astiko.loipes_ydatosylloges || 0;
        monthDatum.astiko.pigadia += row.month[monthIndex].astiko.pigadia || 0;
        monthDatum.astiko.vothroi += row.month[monthIndex].astiko.vothroi || 0;

        monthDatum.fysiko_systima.plimirismenes_ektaseis += row.month[monthIndex].fysiko_systima.plimirismenes_ektaseis || 0;
      });
    });
    this.app.dataUpdated();
  }


  getCurrentData() {
    const currentTime = this.time.current;

    const out: { [hlpcode: number]: RowDataType } = {};

    Object.keys(this.dataPerDimos).forEach(hlpcode => {
      out[hlpcode] = this.dataPerDimos[hlpcode][currentTime.valueMap.year][currentTime.valueMap.month];
    });

    return out;
  }

  getPeFysikaSystimataData() {

    const out: {
      [pe: number]: {
        name: string,
        [year: number]: {
          [month: number]: number
        }
      }
    } = {};

    this.rawData.forEach(row => {
      if (!out[row.code_nom]) {
        out[row.code_nom] = { name: this.peProvider.data.find(pe => pe.code === row.code_nom).name };
      }
      if (!out[row.code_nom][row.year]) {
        out[row.code_nom][row.year] = {};
      }

      Object.keys(row.month).forEach(month => {
        if (!out[row.code_nom][row.year][month]) {
          out[row.code_nom][row.year][month] = 0;
        }
        out[row.code_nom][row.year][month] +=
          row.month[month].fysiko_systima.plimirismenes_ektaseis
          + row.month[month].agrotiko.loipes_ydatosylloges
          + row.month[month].astiko.loipes_ydatosylloges
          ;
      });

    });

    return out;

  }

  getPeRemataKanaliaData() {

    const out: {
      [pe: number]: {
        name: string,
        [year: number]: {
          [month: number]: number
        }
      }
    } = {};

    this.rawData.forEach(row => {
      if (!out[row.code_nom]) {
        out[row.code_nom] = { name: this.peProvider.data.find(pe => pe.code === row.code_nom).name };
      }
      if (!out[row.code_nom][row.year]) {
        out[row.code_nom][row.year] = {};
      }

      Object.keys(row.month).forEach(month => {
        if (!out[row.code_nom][row.year][month]) {
          out[row.code_nom][row.year][month] = 0;
        }
        out[row.code_nom][row.year][month] += row.month[month].agrotiko.remata + row.month[month].agrotiko.kanalia;
      });

    });

    return out;

  }

  getGlobalMaxVauleOfDimosDataByPropertyName(propertyGroup: string, propertyName: string) {
    let maxValue: number = Number.MIN_SAFE_INTEGER;

    const savedValue = this.maxValueMap.find(r => r.group === propertyGroup && r.name === propertyName);

    if (savedValue) {
      return savedValue.value;
    }

    const currentTime = this.time.current;
    Object.keys(this.dataPerDimos).forEach(hlpcode => {

      const d = this.dataPerDimos[parseInt(hlpcode, 10)];
      Object.keys(d).forEach(year => {
        if (year !== 'name') {
          const y = d[parseInt(year, 10)];
          Object.keys(y).forEach(month => {
            const value = y[parseInt(month, 10)][propertyGroup][propertyName];
            if (value > maxValue) {
              maxValue = <number>value;
            }
          });
        }
      });
    });

    this.maxValueMap.push({
      group: propertyGroup,
      name: propertyName,
      value: maxValue
    });

    return maxValue;
  }

}



// WEBPACK FOOTER //
// /Users/sofos/projects/anadigit/kounoupia-frontend/src/app/services/data.service.ts